import { api, toCamelCase, toSnakeCase } from '../lib/api'
import { Invoice, InvoiceCreate, InvoiceStatus, InvoiceUpdate, CorrectionInvoiceCreate } from '../types/invoice'

export interface GetInvoicesParams {
  status?: InvoiceStatus
  search?: string
  dateFrom?: string
  dateTo?: string
  includeArchived?: boolean
  skip?: number
  limit?: number
}

// Convert string decimals to numbers
function parseInvoice(data: Record<string, unknown>): Invoice {
  const invoice = toCamelCase(data) as unknown as Invoice

  // Convert numeric string fields to numbers
  invoice.baseAmount = Number(invoice.baseAmount) || 0
  invoice.totalVat = Number(invoice.totalVat) || 0
  invoice.totalWithholding = Number(invoice.totalWithholding) || 0
  invoice.totalAmount = Number(invoice.totalAmount) || 0

  // Convert items numeric fields
  if (invoice.items) {
    invoice.items = invoice.items.map((item) => ({
      ...item,
      quantity: Number(item.quantity) || 0,
      priceWithoutVat: Number(item.priceWithoutVat) || 0,
      vatRate: Number(item.vatRate) || 0,
      withholdingRate: Number(item.withholdingRate) || 0,
      subtotalWithoutVat: Number(item.subtotalWithoutVat) || 0,
      subtotalWithVat: Number(item.subtotalWithVat) || 0,
    }))
  }

  return invoice
}

// API functions
export const invoiceApi = {
  async getAll(params?: GetInvoicesParams): Promise<Invoice[]> {
    // Convert camelCase params to snake_case for backend
    const queryParams: Record<string, string | number | boolean | undefined> = {}
    if (params?.status) queryParams.status = params.status
    if (params?.search) queryParams.search = params.search
    if (params?.dateFrom) queryParams.date_from = params.dateFrom
    if (params?.dateTo) queryParams.date_to = params.dateTo
    if (params?.includeArchived) queryParams.include_archived = params.includeArchived
    if (params?.skip !== undefined) queryParams.skip = params.skip
    if (params?.limit !== undefined) queryParams.limit = params.limit

    const response = await api.get('/invoices', { params: queryParams })
    return response.data.map((inv: unknown) =>
      parseInvoice(inv as Record<string, unknown>)
    )
  },

  async getById(id: number): Promise<Invoice> {
    const response = await api.get(`/invoices/${id}`)
    return parseInvoice(response.data as Record<string, unknown>)
  },

  async create(data: InvoiceCreate): Promise<Invoice> {
    const payload = toSnakeCase(data as unknown as Record<string, unknown>)
    const response = await api.post('/invoices', payload)
    return parseInvoice(response.data as Record<string, unknown>)
  },

  async update(id: number, data: InvoiceUpdate): Promise<Invoice> {
    const payload = toSnakeCase(data as unknown as Record<string, unknown>)
    const response = await api.put(`/invoices/${id}`, payload)
    return parseInvoice(response.data as Record<string, unknown>)
  },

  async updateStatus(id: number, status: InvoiceStatus): Promise<Invoice> {
    const response = await api.patch(`/invoices/${id}/status`, { status })
    return parseInvoice(response.data as Record<string, unknown>)
  },

  async annul(id: number): Promise<Invoice> {
    const response = await api.post(`/invoices/${id}/annul`)
    return parseInvoice(response.data as Record<string, unknown>)
  },

  async createCorrection(id: number, data: CorrectionInvoiceCreate): Promise<Invoice> {
    const payload = toSnakeCase(data as unknown as Record<string, unknown>)
    const response = await api.post(`/invoices/${id}/correct`, payload)
    return parseInvoice(response.data as Record<string, unknown>)
  },

  async exportCsv(params?: Omit<GetInvoicesParams, 'skip' | 'limit'>): Promise<Blob> {
    const queryParams: Record<string, string | boolean | undefined> = {}
    if (params?.status) queryParams.status = params.status
    if (params?.dateFrom) queryParams.date_from = params.dateFrom
    if (params?.dateTo) queryParams.date_to = params.dateTo
    if (params?.includeArchived) queryParams.include_archived = params.includeArchived

    const response = await api.get('/invoices/export', {
      params: queryParams,
      responseType: 'blob',
    })
    return response.data
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/invoices/${id}`)
  },
}
